import { DatePipe, CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatOption, MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { RouterModule } from '@angular/router';
import { RequestTypeIndicatorComponent } from '../../../common/request-type-indicator/request-type-indicator.component';
import { StatusIndicatorComponent } from '../../../common/status-indicator/status-indicator.component';
import { UserStatus } from '../../../domain/enums/users-status.enum';
import { Icons } from '../../../domain/enums/icons-enums';
import { CustomerResumeBarComponent } from '../../../common/customer-resume-bar/customer-resume-bar.component';
import { IconDetailComponent, IconDetailModel } from '../../../common/icon-detail/icon-detail.component';
import { TaskModel } from '../../../domain/models/task.model';
import { InternalUserInfo } from '../../../domain/models/internal-userInfo-model';
import { UsersServicesService } from '../../../domain/services/users/users-services.service';
import { UserListDataSource } from './users-list.datasource';
import { UserGroup } from '../../../domain/models/user-group.model';
import { MatDialog } from '@angular/material/dialog';
import { AddUserComponent } from '../add-user/add-user.component';
import { CustomDialogFactory } from '../../../domain/services/dialog/custom-dialog.factory';
import { DialogTypeEnum } from '../../../domain/enums/dialog-type.enum';
import { UserGroupServiceService } from '../../../domain/services/user-group/user-group.service.service';
import { DialogService } from '../../../domain/services/dialog/dialog.service';
import { ImpersonationService } from '../../../domain/services/impersonation/impersonation.service';


@Component({
  selector: 'app-users-list',
  standalone: true,
  imports: [
    MatButtonModule,
        MatFormFieldModule,
        MatIconModule,
        MatButtonToggleModule,
        FormsModule,
        MatPaginatorModule,
        RouterModule,
        MatTableModule,
        MatInputModule,
        MatSortModule,
        MatSelectModule,
        CommonModule,
        MatDatepickerModule,
        MatNativeDateModule,
        StatusIndicatorComponent,
        IconDetailComponent,
        MatTableModule,
        MatMenuModule,
        MatTooltipModule,
        MatSnackBarModule,
  ],
  templateUrl: './users-list.component.html',
  styleUrl: './users-list.component.css'
})

export class UsersListComponent implements OnInit, AfterViewInit{

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatTable) table!: MatTable<InternalUserInfo>;

      dataSource = new UserListDataSource([]); 
  displayedColumns = [ 'id','firstName', 'lastName', 'emailAddress', 'phoneNumber','status', 'lastLoginDate','actions'];
  userModel: IconDetailModel | undefined;
    queryStatus: string = "all";
    queryGroup: string = "all";
    query = "";
    icons = Icons 
    userGroups : UserGroup [] = [];
    users: InternalUserInfo[] = []; 
    constructor(private readonly dialogService: DialogService, private readonly service: UsersServicesService,private userGroup: UserGroupServiceService, private readonly dialog: CustomDialogFactory,
      private readonly snackBar: MatSnackBar,
      private readonly impersonation: ImpersonationService) {
    }

    /** A status of 1 means the account is active/enabled. */
    isActive(user: InternalUserInfo): boolean {
      return user.status === 1;
    }

    private notify(message: string, success: boolean = true): void {
      this.snackBar.open(message, 'Close', {
        duration: 4000,
        horizontalPosition: 'right',
        verticalPosition: 'top',
        panelClass: [success ? 'snack-success' : 'snack-error'],
      });
    }

    resetPassword(user: InternalUserInfo): void {
      this.service.resetPassword(user.id).subscribe({
        next: () => this.notify(`Password reset link sent to ${user.userName}`),
        error: () => this.notify('Could not send the password reset link. Please try again.', false),
      });
    }

    toggleStatus(user: InternalUserInfo): void {
      const currentlyActive = this.isActive(user);
      const apply = () => {
        this.service.setStatus(user.id, !currentlyActive).subscribe({
          next: (updated) => {
            // Update the row in place and rebuild the table so the status filter stays in sync.
            const index = this.users.findIndex(u => u.id === user.id);
            if (index > -1) {
              this.users[index] = { ...this.users[index], status: updated?.status ?? (currentlyActive ? 2 : 1) };
            }
            // Reapply status + group + search filters so the refreshed list keeps
            // matching whatever the user currently has filtered on (root cause of a
            // reported bug: the search filter was dropped after disabling a user).
            this.rebuildDataSource(this.applyAllFilters(this.users));
            this.notify(currentlyActive ? `${user.userName} has been disabled` : `${user.userName} has been enabled`);
          },
          error: () => this.notify('Could not update the user status. Please try again.', false),
        });
      };

      if (currentlyActive) {
        // Disabling locks the user out, so confirm first.
        this.dialogService.openConfirmationDialog(
          'Disable this user?',
          `${user.userName} will no longer be able to log in until re-enabled.`,
          (confirmed: boolean) => { if (confirmed) { apply(); } }
        );
      } else {
        apply();
      }
    }

    impersonateUser(user: InternalUserInfo): void {
      this.service.impersonate(user.id).subscribe({
        next: (response) => {
          // start() backs up the admin session, swaps in the target session and reloads home.
          this.impersonation.start(response);
        },
        error: () => {
          // 403 is already surfaced by the auth-error interceptor's snackbar.
        },
      });
    }
  ngOnInit(): void { 
    this.service.getCount().subscribe(value => {
      this.userModel = { icon: Icons.PersonPinIcon, label: 'Total Users', value: value.toString() };
    });
    this.userGroup.getAll().subscribe(value => {
     this.userGroups = value
    });
  }
  ngAfterViewInit(): void {
    this.service.getAll().subscribe(value => {
      this.users = value;

      this.rebuildDataSource(this.applyAllFilters(value));
    });

  }
  mapstatus(status:number){
    if (status == 1){
      return "Active";
    }else{
      return "Inactive";
    }
  }
    rebuildDataSource(data: InternalUserInfo[]) {
      this.dataSource = new UserListDataSource(data); 
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
      this.table.dataSource = this.dataSource;
    }
    // Status and Groups are two separate dropdowns; each used to share the single
    // `queryStatus` model, which meant picking a value in one clobbered the other's
    // selection (the "group dropdown misbehaves / sticks open" bug). They now have
    // independent models and handlers.
    onStatusFilter() {
      this.query = "";
      this.rebuildDataSource(this.applyAllFilters(this.users));
      this.paginator.pageIndex = 0;
    }
    onGroupFilter() {
      this.query = "";
      this.rebuildDataSource(this.applyAllFilters(this.users));
      this.paginator.pageIndex = 0;
    }
    applyStatusFilter(data: InternalUserInfo[]) {
      if (data && data.length > 0
        && this.queryStatus
        && this.queryStatus.length > 0
        && this.queryStatus !== "all") {
          return data.filter(value => this.mapstatus(value.status) === this.queryStatus);
      } else {
        return data;
      }
    }
    applyGroupFilter(data: InternalUserInfo[]) {
      if (data && data.length > 0 && this.queryGroup && this.queryGroup !== "all") {
        const groupId = Number(this.queryGroup);
        return data.filter(value => value.userGroupId === groupId);
      } else {
        return data;
      }
    }
    applyQueryFilter(data: InternalUserInfo[]) {
      if (this.query && this.query.length > 2) {
        const q = this.query.toLowerCase();
        return data.filter(value =>
          value.firstName.toLowerCase().includes(q)
          || value.lastName.toLowerCase().includes(q)
          || value.phoneNumber.toLowerCase().includes(q)
          || value.userName.toLowerCase().includes(q));
      } else {
        return data;
      }
    }
      // Single place that combines every active filter (status, group, search).
      // Every list refresh (search, dropdowns, add/edit/delete/disable) routes through
      // this so no refresh silently drops a filter the user already has applied.
      applyAllFilters(data: InternalUserInfo[]): InternalUserInfo[] {
        return this.applyQueryFilter(this.applyGroupFilter(this.applyStatusFilter(data)));
      }
removeUser(user: InternalUserInfo){

      this.dialogService.openDeletedDialog("Are you sure you want to delete this user from the platform ?","Once you confirm this action, this can’t be undone.", (value: boolean) => {
    if (value){
        this.users = this.users.filter(item => item.id !== user.id);
         this.rebuildDataSource(this.applyAllFilters(this.users));
         this.service.delete(user?.id).subscribe(value => {

          });
    }
        });

}
onSearch() {
  this.paginator.pageIndex = 0; //reset paginator on search
  this.rebuildDataSource(this.applyAllFilters(this.users));
}

addNewUser(){
  let data: InternalUserInfo = {
    firstName: '',
    lastName: '',
    userName: '',
    phoneNumber: '',
    status: 0,
    lastLoginDate: '',
    id: 0,
    email: '',
    password: '',
    userGroupId: 0,
    selected: false,
    paymentOfficeId: 0
  };
  this.dialog.openDialog(DialogTypeEnum.NEW_USER, {}, (data: InternalUserInfo) => {
    this.service.create(data).subscribe(value => {
      this.service.getAll().subscribe(value => {
        this.users = value;

        this.rebuildDataSource(this.applyAllFilters(value));
      });
    });
     });
}

  editUser(user: InternalUserInfo): void {
    // Opens the same dialog in edit mode (it pre-populates from data.user) and
    // persists via update instead of create.
    // NOTE: ManageUserService.openAddUserDialog() already wraps this argument as
    // { user } before handing it to the dialog's MAT_DIALOG_DATA. Passing { user }
    // here double-wrapped it into { user: { user: <user> } }, so AddUserComponent's
    // data.user.id was always undefined -> isEditMode was always false -> the dialog
    // opened as a blank "add" form instead of a populated "edit" form. That was the
    // root cause of the reported "edit user opens with no data" bug.
    this.dialog.openDialog(DialogTypeEnum.NEW_USER, user, (data: InternalUserInfo) => {
      this.service.update(user.id, data).subscribe(() => {
        this.service.getAll().subscribe(value => {
          this.users = value;
          this.rebuildDataSource(this.applyAllFilters(value));
        });
      });
    });
  }

    protected readonly  UserStatusEnum = UserStatus;
} 

 

